/**
 * pickupList.ts (PARTS-PICKUP-01, Seth 2026-09-02: "how can we see the part
 * pickup report for each tech for that day? And then add/edit/remove? Where
 * would that best be managed as part of the every day tasks.")
 *
 * ONE per-tech pickup list for a day, read by three screens — the office's
 * Today page (edit it), the Reports page (print it) and the tech's phone
 * (tick it off). Items come from four places and merge here:
 *   auto    — parts named in a call's dispatch note (ESC) or set on a Pegasus
 *             dispatch (pegasus_dispatch_part): filters, pads, cabinets, gear.
 *   ticket  — an ordered part that has ARRIVED / is IN STOCK and whose return
 *             visit is on this tech's board that day (order_parts tickets).
 *   pickup  — an ESC "Part Pickup" stop the office booked.
 *   office  — anything the office typed in by hand (pegasus_pickup_item).
 * The office can hide an auto item, add its own, and mark items prepped; the
 * tech marks them picked up. Derived items are never stored — only the
 * office's edits and the stamps are (keyed by a stable auto_key).
 *
 * WHERE to pick it up (PARTS-PICKUP-02, Seth 2026-09-03: "this part was
 * ordered on a PO and received at our office, so the system should know it's
 * to be picked up at the office") is never assumed. It is read from the
 * purchase orders behind the stop — a PO number in the stop's notes, or the
 * POs cut for the customers the tech is visiting that day — and the office
 * can override it on the item (pickup_from). Received at the office → "At
 * the office"; shipped Will Call and not received → "Vendor will-call".
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { pagedAll } from "@/lib/pagedAll";
import { displayCustomerName, resolveWindowAndPriority } from "@/lib/format";
import { extractTruckParts, tuneupType, type PartKind, type TruckPart } from "./truckParts";

export type PickupSource = "auto" | "ticket" | "pickup" | "office";
export type PickupWhere = "office" | "vendor" | "unknown";

export interface PickupJob {
  ref: string;                 // ESC dispatch # or Pegasus dispatch id
  source: "esc" | "pegasus";
  custno: string;
  locno: string | null;
  name: string;
  address: string | null;
  city: string | null;
  window: string | null;
  notes: string | null;
  tuneup: string | null;
  isPickupStop: boolean;
}

export interface PickupItem {
  /** Stable identity: auto:<ref>:<partkey> · ticket:<id> · pickup:<ref> · office:<uuid> */
  key: string;
  /** pegasus_pickup_item row id when one exists (office items always; others once stamped/hidden). */
  rowId: string | null;
  source: PickupSource;
  label: string;
  kind: PartKind;
  qty: number;
  note: string | null;
  /** The call this item is for (auto / ticket / pickup); null for a tech-wide office item. */
  job: PickupJob | null;
  hidden: boolean;
  preppedAt: string | null;
  pickedUpAt: string | null;
  /** Where the tech gets it: the office shelf, a vendor counter, or not known yet. */
  where: PickupWhere;
  /** Plain words for the chip — "At the office · PO 20003 received Sep 2". */
  whereLabel: string | null;
  /** What decided it: the office's override, a PO, the ticket's own state, or nothing. */
  whereFrom: "override" | "po" | "ticket" | "none";
  /** The office's explicit choice, when one was made. */
  pickupFrom: "office" | "vendor" | null;
}

export interface PickupTech {
  empno: string;
  name: string;
  jobs: PickupJob[];
  items: PickupItem[];
}

interface StampRow {
  id: string; empno: string; source: string; auto_key: string | null; dispatch_ref: string | null; custno: string | null;
  label: string; qty: number; kind: string; note: string | null; hidden: boolean;
  prepped_at: string | null; picked_up_at: string | null; deleted_at: string | null; pickup_from: "office" | "vendor" | null;
}
/** A purchase order as pickup evidence — Pegasus or ESC, flattened to what decides "where". */
interface PoEvidence {
  ref: string;              // "20003" or ESC "27481"
  custno: string | null;
  vendor: string | null;    // vendor name (code when the name is unknown)
  when: string | null;      // date the PO was cut (YYYY-MM-DD)
  received: boolean;        // fully received (Pegasus received/billed; ESC lines received or billed)
  willCall: boolean;        // ship_via Will Call — the tech picks it up at the vendor
  receivedWhen: string | null;
}

interface EscRow {
  dispatch: string; counter: number | null; window_code: string | null; notes: string | null; custno: string; locno: string | null;
  locname: string | null; customer_fullname: string | null; service_address: string | null; city: string | null;
  tech_empno: string; tech_name: string | null; tech_first: string | null; tech_last: string | null;
}
interface PegRow {
  id: string; assigned_empno: string; custno: string | null; locno: string | null; customer_name: string | null; address: string | null;
  city: string | null; notes: string | null; scheduled_window: string | null; lane_key: string | null; part_state: string | null;
  part_number: string | null; part_po: string | null; summary: string | null;
}
interface EmpRow { empno: string; empname: string | null; firstname: string | null; lastname: string | null }
interface PartRow { dispatch_id: string; label: string; qty: number | null; kind: string | null }

/** ESC pseudo-customers (reference_pseudo_employees / planner 34eq): 0079253 = "Pick Up Part",
 *  0079690 = "Office", 0036012 = "Off Today". PARTS-PICKUP-03 fix: this used to
 *  treat OFFICE stops as part pickups (an oil-change stop showed up as "Part
 *  pickup" on the truck list) and skipped the real Pick Up Part number. */
const PICKUP_CUSTNO = "0079253";
const OFFICE_CUSTNO = "0079690";
const OFF_CUSTNO = "0036012";
const PICKUP_RE = /pick\s*up\s*part|part\s*pick\s*up/i;

export const pickupListKey = (day: string) => ["parts_pickup", day] as const;

export function usePickupDay(day: string, opts?: { empno?: string | null; enabled?: boolean }) {
  return useQuery({
    queryKey: [...pickupListKey(day), opts?.empno ?? "all"],
    enabled: opts?.enabled ?? true,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<PickupTech[]> => {
      const empFilter = opts?.empno ?? null;

      // 1. ESC dispatches for the day (parts read from the notes).
      const esc = await pagedAll<EscRow>(() => {
        // pagedAll needs a FRESH builder per page (builders are mutable).
        const q = supabase.from("v_dispatch_full")
          .select("dispatch, counter, window_code, notes, custno, locno, locname, customer_fullname, service_address, city, tech_empno, tech_name, tech_first, tech_last")
          .eq("appointment_date", day)
          .order("tech_empno", { ascending: true })
          .order("dispatch", { ascending: true });
        return empFilter ? q.eq("tech_empno", empFilter) : q;
      });

      // 2. Pegasus-native scheduled dispatches (structured parts + arrived part tickets).
      let pegQ = supabase.from("pegasus_dispatch")
        .select("id, assigned_empno, custno, locno, customer_name, address, city, notes, scheduled_window, lane_key, part_state, part_number, part_po, summary")
        .eq("scheduled_date", day)
        .is("deleted_at", null)
        .not("assigned_empno", "is", null);
      if (empFilter) pegQ = pegQ.eq("assigned_empno", empFilter);
      const { data: pdisp, error: pErr } = await pegQ;
      if (pErr) throw pErr;
      const pRows = (pdisp ?? []) as PegRow[];

      // 3. Office edits + stamps for the day.
      let stampQ = supabase.from("pegasus_pickup_item")
        .select("id, empno, source, auto_key, dispatch_ref, custno, label, qty, kind, note, hidden, prepped_at, picked_up_at, deleted_at, pickup_from")
        .eq("day", day)
        .is("deleted_at", null);
      if (empFilter) stampQ = stampQ.eq("empno", empFilter);
      const { data: stampsData, error: sErr } = await stampQ;
      if (sErr) throw sErr;
      const stamps = (stampsData ?? []) as StampRow[];

      const ids = pRows.map((d) => d.id);
      const empnos = [...new Set([...pRows.map((d) => d.assigned_empno), ...stamps.map((s) => s.empno)].filter(Boolean))];
      const [partsRes, empsRes] = await Promise.all([
        ids.length ? supabase.from("pegasus_dispatch_part").select("dispatch_id, label, qty, kind").in("dispatch_id", ids) : Promise.resolve({ data: [] as PartRow[] }),
        empnos.length ? supabase.from("mirror_employee").select("empno, empname, firstname, lastname").in("empno", empnos) : Promise.resolve({ data: [] as EmpRow[] }),
      ]);
      const partsByDispatch = new Map<string, TruckPart[]>();
      for (const p of (partsRes.data ?? []) as PartRow[]) {
        const arr = partsByDispatch.get(p.dispatch_id) ?? [];
        arr.push({ key: (p.label || "").toUpperCase(), label: p.label, kind: (p.kind ?? "part") as PartKind, qty: p.qty ?? 1 });
        partsByDispatch.set(p.dispatch_id, arr);
      }
      const empMap = new Map(((empsRes.data ?? []) as EmpRow[]).map((e) => [e.empno, e]));
      const nameOfEmp = (empno: string, fallback?: string | null) => {
        const e = empMap.get(empno);
        return fallback ?? e?.empname ?? (`${e?.firstname ?? ""} ${e?.lastname ?? ""}`.trim() || empno);
      };

      const techs = new Map<string, PickupTech>();
      const techFor = (empno: string, name: string) => {
        let t = techs.get(empno);
        if (!t) { t = { empno, name, jobs: [], items: [] }; techs.set(empno, t); }
        return t;
      };
      const stampByKey = new Map<string, StampRow>();
      for (const s of stamps) if (s.auto_key) stampByKey.set(`${s.empno}|${s.auto_key}`, s);
      const NO_WHERE = { where: "unknown" as PickupWhere, whereLabel: null, whereFrom: "none" as const, pickupFrom: null };
      const applyStamp = (empno: string, item: PickupItem): PickupItem => {
        const s = stampByKey.get(`${empno}|${item.key}`);
        if (!s) return item;
        return { ...item, rowId: s.id, hidden: s.hidden, preppedAt: s.prepped_at, pickedUpAt: s.picked_up_at, note: s.note ?? item.note, qty: s.qty || item.qty, pickupFrom: s.pickup_from ?? null };
      };

      // ESC rows → jobs + auto items (+ vendor pickup stops).
      for (const r of esc) {
        if (r.custno === OFF_CUSTNO) continue; // a day off is not a job
        const name = displayCustomerName(r.locname, r.customer_fullname) || r.custno;
        const isPickupStop = r.custno === PICKUP_CUSTNO
          || PICKUP_RE.test(`${r.locname ?? ""} ${r.customer_fullname ?? ""}`)
          || (r.custno === OFFICE_CUSTNO && PICKUP_RE.test(r.notes ?? ""));
        if (r.custno === OFFICE_CUSTNO && !isPickupStop) continue; // an office stop (meeting, oil change) carries no parts
        const job: PickupJob = {
          ref: r.dispatch, source: "esc", custno: r.custno, locno: r.locno, name: isPickupStop ? "Part Pickup" : name,
          address: r.service_address, city: r.city,
          window: resolveWindowAndPriority(r.window_code, r.notes).timeWindow ?? null,
          notes: r.notes, tuneup: tuneupType(r.notes), isPickupStop,
        };
        const t = techFor(r.tech_empno, nameOfEmp(r.tech_empno, r.tech_name ?? (`${r.tech_first ?? ""} ${r.tech_last ?? ""}`.trim() || null)));
        t.jobs.push(job);
        if (isPickupStop) {
          t.items.push(applyStamp(t.empno, {
            key: `pickup:${r.dispatch}`, rowId: null, source: "pickup",
            label: (r.notes ?? "").replace(/\s+/g, " ").trim().slice(0, 90) || "Part pickup",
            kind: "part", qty: 1, note: null, job, hidden: false, preppedAt: null, pickedUpAt: null, ...NO_WHERE,
          }));
          continue;
        }
        for (const p of extractTruckParts(r.notes)) {
          t.items.push(applyStamp(t.empno, { key: `auto:${r.dispatch}:${p.key}`, rowId: null, source: "auto", label: p.label, kind: p.kind, qty: p.qty ?? 1, note: null, job, hidden: false, preppedAt: null, pickedUpAt: null, ...NO_WHERE }));
        }
      }

      // Pegasus rows → jobs + structured auto items + arrived-part tickets.
      for (const d of pRows) {
        const job: PickupJob = {
          ref: d.id, source: "pegasus", custno: d.custno ?? "", locno: d.locno, name: d.customer_name ?? d.custno ?? "Customer",
          address: d.address, city: d.city, window: d.scheduled_window, notes: d.notes, tuneup: tuneupType(d.notes), isPickupStop: false,
        };
        const t = techFor(d.assigned_empno, nameOfEmp(d.assigned_empno));
        t.jobs.push(job);
        const structured = partsByDispatch.get(d.id);
        const parts = structured && structured.length ? structured : extractTruckParts(d.notes);
        for (const p of parts) {
          t.items.push(applyStamp(t.empno, { key: `auto:${d.id}:${p.key}`, rowId: null, source: "auto", label: p.label, kind: p.kind, qty: p.qty ?? 1, note: null, job, hidden: false, preppedAt: null, pickedUpAt: null, ...NO_WHERE }));
        }
        if (d.lane_key === "order_parts" && (d.part_state === "arrived" || d.part_state === "in_stock")) {
          const what = d.part_number || d.summary || "Ordered part";
          t.items.push(applyStamp(t.empno, {
            key: `ticket:${d.id}`, rowId: null, source: "ticket",
            label: what, kind: "part", qty: 1,
            note: [d.part_state === "in_stock" ? "In stock" : "Arrived", d.part_po ? `PO ${d.part_po}` : null].filter(Boolean).join(" · "),
            job, hidden: false, preppedAt: null, pickedUpAt: null,
            // An arrived / in-stock ticket is on the office shelf by definition.
            where: "office", whereLabel: d.part_state === "in_stock" ? "At the office · in stock" : `At the office${d.part_po ? ` · PO ${d.part_po}` : ""}`, whereFrom: "ticket", pickupFrom: null,
          }));
        }
      }

      // Office items (typed in by hand) — attach to a job when a ref matches.
      for (const s of stamps) {
        if (s.source !== "office") continue;
        const t = techFor(s.empno, nameOfEmp(s.empno));
        const job = s.dispatch_ref ? t.jobs.find((j) => j.ref === s.dispatch_ref) ?? null : null;
        t.items.push({
          key: `office:${s.id}`, rowId: s.id, source: "office", label: s.label, kind: (s.kind || "part") as PartKind, qty: s.qty,
          note: s.note, job, hidden: false, preppedAt: s.prepped_at, pickedUpAt: s.picked_up_at, ...NO_WHERE, pickupFrom: s.pickup_from ?? null,
        });
      }

      // 4. WHERE does each pickup happen? Read the purchase orders behind it.
      await resolvePickupWhere([...techs.values()]);

      return [...techs.values()]
        .map((t) => ({ ...t, items: t.items.sort(sortItems) }))
        .sort((a, b) => visibleCount(b) - visibleCount(a) || a.name.localeCompare(b.name));
    },
  });
}

const PO_IN_NOTES = /\bP\.?O\.?\s*#?\s*(\d{4,8})\b/gi;
const escPoKey = (po: string) => po.replace(/^0+/, "") || po;

/**
 * Attach a "where" to every pickup-stop and office item from PO evidence.
 * Order of proof: the office's override → a PO number written in the stop's
 * notes → the most recent PO cut for a customer the tech visits that day.
 */
async function resolvePickupWhere(techs: PickupTech[]): Promise<void> {
  const candidates = techs.flatMap((t) => t.items.filter((i) => i.source === "pickup" || i.source === "office"));
  if (candidates.length === 0) return;
  const evidence = await pickupPoEvidence(techs);
  const fmt = (d: string | null) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");
  const describe = (e: PoEvidence): { where: PickupWhere; label: string } => {
    if (e.received) return { where: "office", label: `At the office · PO ${e.ref}${e.receivedWhen ? ` received ${fmt(e.receivedWhen)}` : " received"}` };
    if (e.willCall) return { where: "vendor", label: `Vendor will-call${e.vendor ? ` at ${e.vendor}` : ""} · PO ${e.ref}` };
    return { where: "office", label: `Expected at the office · PO ${e.ref} not received yet${e.vendor ? ` (${e.vendor})` : ""}` };
  };
  for (const t of techs) {
    const techCustnos = new Set(t.jobs.filter((j) => !j.isPickupStop && j.custno).map((j) => j.custno));
    for (const i of t.items) {
      if (i.source !== "pickup" && i.source !== "office") continue;
      const text = `${i.label} ${i.note ?? ""} ${i.job?.notes ?? ""}`;
      const mentioned = new Set([...text.matchAll(PO_IN_NOTES)].map((m) => m[1]));
      let hit = evidence.find((e) => mentioned.has(e.ref));
      if (!hit && i.source === "pickup") hit = evidence.find((e) => e.custno && techCustnos.has(e.custno));
      if (!hit && i.source === "office" && i.job?.custno) hit = evidence.find((e) => e.custno === i.job!.custno);
      if (i.pickupFrom) {
        // The office said so — keep the PO as context when we have one.
        i.where = i.pickupFrom; i.whereFrom = "override";
        i.whereLabel = i.pickupFrom === "office" ? `At the office${hit ? ` · PO ${hit.ref}` : ""}` : `Vendor will-call${hit?.vendor ? ` at ${hit.vendor}` : ""}${hit ? ` · PO ${hit.ref}` : ""}`;
        continue;
      }
      if (hit) {
        const d = describe(hit);
        i.where = d.where; i.whereLabel = d.label; i.whereFrom = "po";
      }
    }
  }
}

/** The purchase orders that could explain a pickup: mentioned by number, or cut for a customer the tech visits that day. */
async function pickupPoEvidence(techs: PickupTech[]): Promise<PoEvidence[]> {
  const custnos = new Set<string>();
  const poNums = new Set<string>();
  for (const t of techs) {
    for (const j of t.jobs) if (!j.isPickupStop && j.custno) custnos.add(j.custno);
    for (const i of t.items) {
      if (i.source !== "pickup" && i.source !== "office") continue;
      const text = `${i.label} ${i.note ?? ""} ${i.job?.notes ?? ""}`;
      for (const m of text.matchAll(PO_IN_NOTES)) poNums.add(m[1]);
      if (i.job?.custno && !i.job.isPickupStop) custnos.add(i.job.custno);
    }
  }
  if (custnos.size === 0 && poNums.size === 0) return [];
  const since = new Date(Date.now() - 120 * 86400 * 1000).toISOString().slice(0, 10);
  const custList = [...custnos];
  const nums = [...poNums];
  const padded = nums.map((n) => n.padStart(15, "0"));

  const pegQ = supabase.from("pegasus_po")
    .select("po_number, custno, status, ship_via, vendor_code, date_requested, created_at, received_at")
    .gte("created_at", `${since}T00:00:00`)
    .neq("status", "cancelled");
  const escQ = supabase.from("mirror_po")
    .select("po, custno, status, billed, ship_via, vendor_code, date")
    .gte("date", since);
  const orParts = (col: string, list: string[]) => list.length ? `${col}.in.(${list.map((x) => `"${x}"`).join(",")})` : null;
  const pegOr = [orParts("custno", custList), orParts("po_number", nums)].filter(Boolean).join(",");
  const escOr = [orParts("custno", custList), orParts("po", padded)].filter(Boolean).join(",");
  const [pegRes, escRes] = await Promise.all([
    pegOr ? pegQ.or(pegOr).limit(200) : Promise.resolve({ data: [] }),
    escOr ? escQ.or(escOr).limit(200) : Promise.resolve({ data: [] }),
  ]);
  type PegPo = { po_number: string | null; custno: string | null; status: string; ship_via: string | null; vendor_code: string | null; date_requested: string | null; created_at: string; received_at: string | null };
  type EscPo = { po: string; custno: string | null; status: string | null; billed: boolean | null; ship_via: string | null; vendor_code: string | null; date: string | null };
  const peg = ((pegRes.data ?? []) as PegPo[]);
  const esc = ((escRes.data ?? []) as EscPo[]);
  if (peg.length === 0 && esc.length === 0) return [];

  // ESC receiving lives on the lines: received >= ordered on every line.
  const escReceived = new Map<string, boolean>();
  if (esc.length) {
    const { data: led } = await supabase.from("mirror_po_led").select("po, quan, received").in("po", esc.map((e) => e.po)).limit(2000);
    const agg = new Map<string, { q: number; r: number }>();
    for (const l of (led ?? []) as { po: string; quan: number | null; received: number | null }[]) {
      const a = agg.get(l.po) ?? { q: 0, r: 0 };
      a.q += Number(l.quan ?? 0); a.r += Number(l.received ?? 0);
      agg.set(l.po, a);
    }
    for (const [po, a] of agg) escReceived.set(po, a.q > 0 && a.r >= a.q);
  }
  const vendorCodes = [...new Set([...peg.map((p) => p.vendor_code), ...esc.map((e) => e.vendor_code)].filter(Boolean))] as string[];
  const vendorName = new Map<string, string>();
  if (vendorCodes.length) {
    const { data: v } = await supabase.from("mirror_vendor").select("vendor_code, name").in("vendor_code", vendorCodes);
    for (const r of (v ?? []) as { vendor_code: string; name: string | null }[]) if (r.name) vendorName.set(r.vendor_code, r.name);
  }
  const isWillCall = (s: string | null) => /will\s*call/i.test(s ?? "");
  const evidence: PoEvidence[] = [
    ...peg.map((p) => ({
      ref: p.po_number ?? "", custno: p.custno, vendor: p.vendor_code ? vendorName.get(p.vendor_code) ?? p.vendor_code : null,
      when: p.created_at.slice(0, 10), received: p.status === "received" || p.status === "billed", willCall: isWillCall(p.ship_via),
      receivedWhen: p.received_at ? p.received_at.slice(0, 10) : null,
    })),
    ...esc.map((e) => ({
      ref: escPoKey(e.po), custno: e.custno, vendor: e.vendor_code ? vendorName.get(e.vendor_code) ?? e.vendor_code : null,
      when: e.date, received: (escReceived.get(e.po) ?? false) || e.billed === true, willCall: isWillCall(e.ship_via), receivedWhen: null,
    })),
  ].filter((e) => e.ref);
  evidence.sort((a, b) => (b.when ?? "").localeCompare(a.when ?? ""));
  return evidence;
}

const SOURCE_ORDER: Record<PickupSource, number> = { ticket: 0, pickup: 1, office: 2, auto: 3 };
function sortItems(a: PickupItem, b: PickupItem) {
  return SOURCE_ORDER[a.source] - SOURCE_ORDER[b.source] || a.label.localeCompare(b.label);
}
export const visibleItems = (t: PickupTech) => t.items.filter((i) => !i.hidden);
const visibleCount = (t: PickupTech) => visibleItems(t).length;

/**
 * PARTS-PICKUP-03 (Seth 2026-09-15: "Brent is done with his calls for the day.
 * But his screen still shows the parts that are needed"): an item for a call
 * that is already wrapped is history, not a to-do. `openItems` is what the
 * tech still has to grab — items for calls not yet done, plus the office's
 * "for the truck" items.
 */
export const openItems = (t: PickupTech, doneRefs: Set<string>) =>
  visibleItems(t).filter((i) => !(i.job && doneRefs.has(i.job.ref)));

/** The next weekday after `fromISO` — where "tomorrow's parts" come from once today is done. */
export function nextPickupDay(fromISO: string): string {
  const d = new Date(`${fromISO}T00:00:00`);
  do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
  return d.toLocaleDateString("en-CA");
}

/** "3× 20x25 F100" — the consolidated grab list for one tech. */
export function grabList(t: PickupTech): { label: string; kind: PartKind; n: number }[] {
  const map = new Map<string, { label: string; kind: PartKind; n: number }>();
  for (const i of visibleItems(t)) {
    if (i.source === "pickup") continue;
    const k = i.label.toUpperCase();
    const e = map.get(k);
    if (e) e.n += i.qty; else map.set(k, { label: i.label, kind: i.kind, n: i.qty });
  }
  return [...map.values()].sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
}

// ── Mutations ────────────────────────────────────────────────────────────────

async function who(): Promise<string> {
  return (await supabase.auth.getUser()).data.user?.email ?? "office";
}

export function useAddPickupItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { day: string; empno: string; label: string; qty?: number; kind?: PartKind; note?: string | null; dispatchRef?: string | null; custno?: string | null }) => {
      const { error } = await supabase.from("pegasus_pickup_item").insert({
        day: v.day, empno: v.empno, source: "office", label: v.label.trim(), qty: v.qty ?? 1, kind: v.kind ?? "part",
        note: v.note?.trim() || null, dispatch_ref: v.dispatchRef ?? null, custno: v.custno ?? null, created_by: await who(),
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: pickupListKey(v.day) }),
  });
}

export function useUpdatePickupItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { day: string; rowId: string; patch: { label?: string; qty?: number; note?: string | null; kind?: PartKind } }) => {
      const { error } = await supabase.from("pegasus_pickup_item")
        .update({ ...v.patch, updated_at: new Date().toISOString() }).eq("id", v.rowId);
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: pickupListKey(v.day) }),
  });
}

/** Office removes an item: office rows soft-delete; derived rows get a hidden stamp. */
export function useRemovePickupItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { day: string; empno: string; item: PickupItem }) => {
      const me = await who();
      if (v.item.source === "office" && v.item.rowId) {
        const { error } = await supabase.from("pegasus_pickup_item")
          .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq("id", v.item.rowId);
        if (error) throw error;
        return;
      }
      const { error } = await supabase.from("pegasus_pickup_item").upsert({
        day: v.day, empno: v.empno, source: v.item.source === "office" ? "office" : v.item.source === "ticket" ? "ticket" : "auto",
        auto_key: v.item.key, dispatch_ref: v.item.job?.ref ?? null, custno: v.item.job?.custno ?? null,
        label: v.item.label, qty: v.item.qty, kind: v.item.kind, hidden: true, created_by: me, updated_at: new Date().toISOString(),
      }, { onConflict: "day,empno,auto_key" });
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: pickupListKey(v.day) }),
  });
}

/** Put a hidden derived item back. */
export function useRestorePickupItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { day: string; rowId: string }) => {
      const { error } = await supabase.from("pegasus_pickup_item").update({ hidden: false, updated_at: new Date().toISOString() }).eq("id", v.rowId);
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: pickupListKey(v.day) }),
  });
}

/** PARTS-PICKUP-02: the office says where it's picked up (office shelf / vendor will-call / let the POs decide). */
export function useSetPickupFrom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { day: string; empno: string; item: PickupItem; from: "office" | "vendor" | null }) => {
      const me = await who();
      const now = new Date().toISOString();
      if (v.item.rowId) {
        const { error } = await supabase.from("pegasus_pickup_item").update({ pickup_from: v.from, updated_at: now }).eq("id", v.item.rowId);
        if (error) throw error;
        return;
      }
      const { error } = await supabase.from("pegasus_pickup_item").upsert({
        day: v.day, empno: v.empno, source: v.item.source === "ticket" ? "ticket" : "auto", auto_key: v.item.key,
        dispatch_ref: v.item.job?.ref ?? null, custno: v.item.job?.custno ?? null,
        label: v.item.label, qty: v.item.qty, kind: v.item.kind, hidden: false, created_by: me, updated_at: now, pickup_from: v.from,
      }, { onConflict: "day,empno,auto_key" });
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: pickupListKey(v.day) }),
  });
}

/** Prepped (office / warehouse) or picked up (tech) — one upsert, keyed by the item's stable key.
 *  PARTS-PICKUP-03: the tick shows INSTANTLY (optimistic) — the list used to
 *  wait for a 1–3 s refetch, so techs tapped twice and the second tap undid
 *  the first ("they kept unchecking"). Rolled back only if the write fails. */
export function useStampPickupItem() {
  const qc = useQueryClient();
  return useMutation({
    onMutate: async (v: { day: string; empno: string; item: PickupItem; field: "prepped" | "picked"; on: boolean }) => {
      await qc.cancelQueries({ queryKey: pickupListKey(v.day) });
      const snapshots = qc.getQueriesData<PickupTech[]>({ queryKey: pickupListKey(v.day) });
      const now = new Date().toISOString();
      qc.setQueriesData<PickupTech[]>({ queryKey: pickupListKey(v.day) }, (old) =>
        old?.map((t) => t.empno !== v.empno ? t : {
          ...t,
          items: t.items.map((i) => i.key !== v.item.key ? i : (v.field === "prepped" ? { ...i, preppedAt: v.on ? now : null } : { ...i, pickedUpAt: v.on ? now : null })),
        }));
      return { snapshots };
    },
    onError: (_e, _v, ctx) => {
      for (const [key, data] of ctx?.snapshots ?? []) qc.setQueryData(key, data);
    },
    onSettled: (_d, _e, v) => qc.invalidateQueries({ queryKey: pickupListKey(v.day) }),
    mutationFn: async (v: { day: string; empno: string; item: PickupItem; field: "prepped" | "picked"; on: boolean }) => {
      const me = await who();
      const now = new Date().toISOString();
      const stamp = v.field === "prepped"
        ? { prepped_at: v.on ? now : null, prepped_by: v.on ? me : null }
        : { picked_up_at: v.on ? now : null, picked_up_by: v.on ? me : null };
      if (v.item.rowId) {
        const { error } = await supabase.from("pegasus_pickup_item").update({ ...stamp, updated_at: now }).eq("id", v.item.rowId);
        if (error) throw error;
        return;
      }
      const { error } = await supabase.from("pegasus_pickup_item").upsert({
        day: v.day, empno: v.empno, source: v.item.source === "ticket" ? "ticket" : "auto", auto_key: v.item.key,
        dispatch_ref: v.item.job?.ref ?? null, custno: v.item.job?.custno ?? null,
        label: v.item.label, qty: v.item.qty, kind: v.item.kind, hidden: false, created_by: me, updated_at: now, ...stamp,
      }, { onConflict: "day,empno,auto_key" });
      if (error) throw error;
    },
  });
}
